import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../../lib/AuthContext";
import {
  BookmarkSimple, MagnifyingGlass, CircleNotch, ArrowLeft, ArrowRight,
  Eye, Trash, DownloadSimple, Lock, Crown, CaretUp, CaretDown, Broadcast,
} from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";

export default function DashboardSaved() {
  const { user, authFetch } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("saved_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, sort_by: sortBy, sort_dir: sortDir, page, per_page: 25 });
      const res = await authFetch(`${window.location.origin}/api/dashboard/saved?${params}`);
      if (res.ok) {
        const d = await res.json();
        setItems(d.items); setTotal(d.total); setTotalPages(d.total_pages); setLimit(d.limit);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authFetch, search, sortBy, sortDir, page]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
    setPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return null;
    return sortDir === "asc" ? <CaretUp className="w-3 h-3" /> : <CaretDown className="w-3 h-3" />;
  };

  const handleRemove = async (npi) => {
    setRemoving(npi);
    try {
      await authFetch(`${window.location.origin}/api/dashboard/saved/remove/${npi}`, { method: "DELETE" });
      setItems(prev => prev.filter(i => i.npi !== npi));
      setTotal(prev => prev - 1);
    } catch (e) { console.error(e); }
    finally { setRemoving(null); }
  };

  const tier = user?.tier || "free";
  const canExport = tier !== "free";
  const usagePercent = limit > 0 && limit !== -1 ? Math.min(100, Math.round((total / limit) * 100)) : 0;

  const exportCsv = () => {
    if (!canExport) return;
    const hdr = "Provider Name,NPI,Specialty,State,Saved At\n";
    const rows = items.map(i => `"${i.provider_name || ""}","${i.npi}","${i.specialty || ""}","${i.state || ""}","${i.saved_at || ""}"`).join("\n");
    const blob = new Blob([hdr + rows], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "saved-providers.csv"; a.click();
  };

  const sortableColumns = [
    { key: "provider_name", label: "Provider" },
    { key: "npi", label: "NPI" },
    { key: "specialty", label: "Specialty" },
    { key: "state", label: "State" },
    { key: "saved_at", label: "Saved" },
  ];

  return (
    <>
      <Helmet><title>Saved Providers | NPI Lookup</title></Helmet>
      <div className="" data-testid="dashboard-saved">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display font-bold text-2xl text-slate-900">Saved Providers</h1>
            <p className="text-sm text-slate-500">
              {total} saved{limit > 0 && limit !== -1 ? ` of ${limit} limit` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!canExport ? (
              <Link to="/pricing"><Button variant="outline" size="sm" className="gap-2 rounded-lg text-slate-500"><Lock className="w-4 h-4" /> Export (Pro+)</Button></Link>
            ) : (
              <Button onClick={exportCsv} variant="outline" size="sm" className="gap-2 rounded-lg"><DownloadSimple className="w-4 h-4" /> Export CSV</Button>
            )}
          </div>
        </div>

        {/* Usage bar */}
        {limit > 0 && limit !== -1 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-slate-600">{total} of {limit} providers saved</span>
              {total >= limit && (
                <Link to="/pricing" className="text-teal font-medium text-xs flex items-center gap-1 hover:underline">
                  <Crown className="w-3 h-3" weight="fill" /> Upgrade for more
                </Link>
              )}
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${usagePercent >= 90 ? "bg-red-500" : usagePercent >= 70 ? "bg-amber-500" : "bg-teal/100"}`} style={{ width: `${usagePercent}%` }} />
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-4 max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search saved providers..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal/30"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center"><CircleNotch className="w-6 h-6 text-teal animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <BookmarkSimple className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="font-display font-semibold text-slate-900 mb-1">No saved providers yet</h3>
              <p className="text-sm text-slate-500 mb-4">Search for a provider and click the Save button to bookmark them here.</p>
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-teal font-medium hover:underline">
                <MagnifyingGlass className="w-4 h-4" /> Search Providers
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {sortableColumns.map(({ key, label }) => (
                      <th key={key} onClick={() => toggleSort(key)}
                        className="text-left px-4 py-3 font-semibold text-slate-600 cursor-pointer select-none hover:text-slate-900">
                        <span className="flex items-center gap-1">{label} <SortIcon col={key} /></span>
                      </th>
                    ))}
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.npi} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <Link to={`/provider/${item.npi}`} className="font-medium text-slate-900 hover:text-teal">{item.provider_name || "—"}</Link>
                      </td>
                      <td className="px-4 py-3 font-mono text-slate-600 text-xs">{item.npi}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-[160px] truncate">{item.specialty || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{item.state || "—"}</td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                        {item.saved_at ? new Date(item.saved_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/provider/${item.npi}`} className="text-slate-400 hover:text-teal" title="View Profile"><Eye className="w-4 h-4" /></Link>
                          <button onClick={() => handleRemove(item.npi)} disabled={removing === item.npi}
                            className="text-slate-400 hover:text-red-500" title="Remove" data-testid={`unsave-${item.npi}`}>
                            {removing === item.npi ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Trash className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="gap-1 rounded-lg"><ArrowLeft className="w-4 h-4" /> Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="gap-1 rounded-lg">Next <ArrowRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
